/**
 * Settings page — user account + preferences hub.
 *
 * For now this hosts account identity + the sign-out control. As
 * the app grows we'll slot per-user preferences here (default
 * video provider, default channel, per-channel API keys, etc.)
 * rather than sprinkling them across each feature page.
 */
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { logout } from "@/lib/api";

export default function Settings() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      /* ignore — we're logging out anyway */
    }
    setUser(null);
    toast.success("Signed out");
    navigate("/login", { replace: true });
  };

  return (
    <div
      data-testid="settings-page"
      className="px-6 lg:px-10 py-8 max-w-[900px] mx-auto"
    >
      <div className="mb-8">
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-yellow-400 mb-2">
          account
        </div>
        <h1 className="text-white text-4xl font-black mb-2">Settings</h1>
        <p className="text-zinc-400 text-sm max-w-2xl">
          Your account, preferences and integrations.
        </p>
      </div>

      <section
        data-testid="settings-account-section"
        className="border border-zinc-800 rounded-sm bg-zinc-950/40 p-6 mb-6"
      >
        <h2 className="font-mono text-[11px] uppercase tracking-[0.25em] text-yellow-400 mb-4">
          Account
        </h2>
        <div className="flex items-center gap-4 mb-6">
          {user?.picture ? (
            <img
              src={user.picture}
              alt={user.name}
              className="h-16 w-16 rounded-full border border-zinc-800"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="h-16 w-16 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center text-yellow-400 font-black text-xl">
              {user?.name?.[0]?.toUpperCase() || "?"}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div
              data-testid="settings-account-name"
              className="text-white text-lg font-bold truncate"
            >
              {user?.name || "Operator"}
            </div>
            <div
              data-testid="settings-account-email"
              className="text-zinc-400 text-sm font-mono truncate"
            >
              {user?.email || "—"}
            </div>
            {user?.is_admin && (
              <div className="mt-1 inline-block font-mono text-[10px] uppercase tracking-widest text-black bg-yellow-400 px-2 py-0.5 rounded-sm">
                Admin
              </div>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={handleLogout}
          data-testid="settings-logout-btn"
          className="font-mono text-xs uppercase tracking-wider px-4 py-2 border border-zinc-700 text-zinc-300 rounded-sm hover:border-red-500 hover:text-red-400 transition-colors"
        >
          Sign out
        </button>
      </section>

      <section
        data-testid="settings-preferences-section"
        className="border border-zinc-800 rounded-sm bg-zinc-950/40 p-6"
      >
        <h2 className="font-mono text-[11px] uppercase tracking-[0.25em] text-yellow-400 mb-4">
          Preferences
        </h2>
        <p className="text-zinc-500 text-sm">
          Per-user preferences (default video provider, default channel,
          BYOK integrations) will appear here. Nothing to configure yet.
        </p>
      </section>
    </div>
  );
}
